import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { calPaginatorRecord } from "helpers/paginator";
import { toast } from "react-toastify";

const templatePoliciesUrl = "/template_policies"

export const getTemplatePolicies = createAsyncThunk(
  "TemplatePolicies/getTemplatePolicies",
  async (params) => {
    try {
      const response = await apiAxios.get(`${templatePoliciesUrl}`, { params } )
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const createTemplatePolicy = createAsyncThunk(
  "TemplatePolicies/createTemplatePolicy",
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(templatePoliciesUrl, data)
      return response.data
    }
    catch(error){
      return rejectWithValue(error.response.data)
    }
  }
)

export const updateTemplatePolicy = createAsyncThunk(
  "TemplatePolicies/updateTemplatePolicy",
  async ({ assetId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${templatePoliciesUrl}/${assetId}`, data)
      return response.data
    }
    catch(error){
      const errorData = error.response?.data || { message: error.message }
      return rejectWithValue({
        error: errorData.error || errorData.message || 'Upload failed',
        message: errorData.message || 'Failed to upload files'
      })
    }
  }
)

export const deleteTemplatePolicy = createAsyncThunk(
  "TemplatePolicies/deleteTemplatePolicy",
  async (assetId, { rejectWithValue }) => {
    try {
      const response = await apiAxios.delete(`${templatePoliciesUrl}/${assetId}`);
      return response.data

    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message })
    }
  }
)

export const uploadTemplatePolicy = createAsyncThunk(
  "TemplatePolicies/uploadTemplatePolicy",
  async ({assetId, data}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${templatePoliciesUrl}/${assetId}/upload`, data, {
        headers: { 'Content-Type': 'multipart/form-data'}
      })
      return response.data
    }
    catch(error){
      const errorData = error.response?.data || { message: error.message }
      return rejectWithValue({
        error: errorData.error || errorData.message || 'Upload failed',
        message: errorData.message || 'Failed to upload files'
      })
    }
  }
)

export const downloadTemplatePolicy = createAsyncThunk(
  "TemplatePolicies/downloadTemplatePolicy",
  async ({assetId, fileName}) => {
    try {
      const response = await apiAxios.get(`${templatePoliciesUrl}/${assetId}/download`, { responseType: "blob"});

      // Create a blob URL for the downloaded file
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      return;

    } catch (error) {
      throw new Error(error.message)
    }
  }
)

export const getAncestors = createAsyncThunk(
  "TemplatePolicies/getAncestors",
  async (assetId, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${templatePoliciesUrl}/${assetId}/ancestors`)
      return response.data
    }
    catch(error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const getEditorConfig = createAsyncThunk(
  "TemplatePolicies/getEditorConfig",
  async ({ assetId, forceView = false }, { rejectWithValue }) => {
    try {
      const params = forceView ? { force_view: true } : {}
      const response = await apiAxios.get(`${templatePoliciesUrl}/${assetId}/editor_config`, { params })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data)
    }
  }
)

export const shareTemplatePolicy = createAsyncThunk(
  "TemplatePolicies/shareTemplatePolicy",
  async ({ assetId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${templatePoliciesUrl}/${assetId}/share`, data)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data)
    }
  }
)

const handlePayload = (state, action, type="none") => {
  const { error, data, message } = action.payload;

  if (error !== undefined) {
    toast.error(error);
  } else {
    const record = data.attributes

    if (record !== null) {
      state.assetData = record

      switch (type) {
        case "create":
          state.data.unshift(record)
          break
        case "delete":
          state.data = state.data.filter((item) => item.id !== record.id)
          break;
        default:
        {
          const updatedIndex = state.data.findIndex((item) => item.id === record.id)
          if (updatedIndex !== -1) {
            state.data[updatedIndex] = record
          }

          break;
        }
      }
    }
    toast.success(message);
  }
};

const templatePoliciesSlice = createSlice({
  name: "templatePolicies",
  initialState: {
    data: [],
    totalRows: 0,
    paginate: {
      totalPagesCount: 0,
      currentPage: 1,
      pageSize: 100,
      fromRecord: 0,
      toRecord: 1,
    },
    currentParams: {
      page: 1,
      per_page: 100,
      sort: {
        attribute: 'name',
        direction: 'asc'
      }
    },
    sortParams: {attribute: 'name', direction: 'asc'},
    loading: false,
    downloading: false,
    uploading: false,
    ancestorData: [],
    currentFolder: null,
    assetData: null,
  },
  reducers: {
    setCurrentPage: (state, action) => {
      state.paginate.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.paginate.pageSize = parseInt(action.payload)
    },
    setCurrentParams: (state, action) => {
      state.currentParams = action.payload
    },
    setSortParams: (state, action) => {
      state.sortParams = action.payload
    },
    resetState: (state) => {
      state.data = []
      state.totalRows = 0
      state.loading = false
      state.downloading = false
      state.uploading = false
      state.ancestorData = []
      state.currentFolder = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Get template policies list
      .addCase(getTemplatePolicies.pending, (state) => {
        state.loading = true
      })
      .addCase(getTemplatePolicies.fulfilled, (state, action) => {
        const { rows, total_rows, current_asset } = action.payload

        state.data = rows.map((row) => row.attributes)
        state.totalRows = total_rows
        state.currentFolder = current_asset
        state.loading = false
        state.paginate = {
          ...state.paginate,
          ...calPaginatorRecord({
            total: total_rows,
            currentPage: state.currentParams.page,
            perPage: state.currentParams.per_page
          })
        }
      })
      .addCase(getTemplatePolicies.rejected, (state) => {
        state.loading = false
      })

      // Create folder
      .addCase(createTemplatePolicy.pending, (state) => {
        state.loading = true
      })
      .addCase(createTemplatePolicy.fulfilled, (state, action) => {
        handlePayload(state, action, "create")
        state.loading = false
      })
      .addCase(createTemplatePolicy.rejected, (state, action) => {
        const { error } = action.payload || {}
        if (error) toast.error(error)
        state.loading = false
      })

      // Update folder
      .addCase(updateTemplatePolicy.pending, (state) => {
        state.loading = true
      })
      .addCase(updateTemplatePolicy.fulfilled, (state, action) => {
        handlePayload(state, action, "update")
        state.loading = false
      })
      .addCase(updateTemplatePolicy.rejected, (state, action) => {
        const { error } = action.payload || {}
        if (error) toast.error(error)
        state.loading = false
      })

      // Delete
      .addCase(deleteTemplatePolicy.pending, (state) => {
        state.loading = true
      })
      .addCase(deleteTemplatePolicy.fulfilled, (state, action) => {
        handlePayload(state, action, "delete")
        state.loading = false
      })
      .addCase(deleteTemplatePolicy.rejected, (state, action) => {
        const payload = action.payload || {}
        const errorMessage = payload.message || payload.error || 'Failed to delete item'
        toast.error(errorMessage)
        state.loading = false
      })

      // Upload
      .addCase(uploadTemplatePolicy.pending, (state) => {
        state.uploading = true
      })
      .addCase(uploadTemplatePolicy.fulfilled, (state, action) => {
        const { data, message } = action.payload
        if (Array.isArray(data)) {
          data.forEach(item => {
            state.data.unshift(item.attributes)
          })
        }
        toast.success(message)
        state.uploading = false
      })
      .addCase(uploadTemplatePolicy.rejected, (state, action) => {
        const payload = action.payload || {}
        const errorMessage = payload.error || payload.message || 'Failed to upload files'
        toast.error(errorMessage)
        state.uploading = false
      })

      // Download
      .addCase(downloadTemplatePolicy.pending, (state) => {
        state.downloading = true
      })
      .addCase(downloadTemplatePolicy.fulfilled, (state) => {
        state.downloading = false
        toast.success("Download completed successfully")
      })
      .addCase(downloadTemplatePolicy.rejected, (state) => {
        state.downloading = false
        toast.error("Download failed")
      })

      // Get ancestors
      .addCase(getAncestors.fulfilled, (state, action) => {
        state.ancestorData = action.payload.map((row) => row.attributes)
      })

      // Share / assign owner
      .addCase(shareTemplatePolicy.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(shareTemplatePolicy.rejected, (state, action) => {
        const payload = action.payload || {}
        toast.error(payload.message || payload.error || 'Failed to assign owner')
      })
  }
});

export const {
  setCurrentPage,
  setPageSize,
  setCurrentParams,
  setSortParams,
  resetState
} = templatePoliciesSlice.actions;

export default templatePoliciesSlice.reducer;
